import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Globe2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
interface SlideTractionProps {
  onContinue: () => void;
}
type Account = {
  name: string;
  closed?: string;
  active?: string;
  won: number;
  activeCount: number;
  fullPotential: string;
  desc: string;
  countries?: string;
};
type Stage = {
  num: 'I' | 'II' | 'III';
  label: string;
  range: string;
  accent: string; // tailwind bg utility for the stage label
  bgTint: string;
  totals: {
    closed?: string;
    active: string;
    growth?: string;
  };
  accounts: Account[];
};
const STAGES: Stage[] = [{
  num: 'III',
  label: 'Enterprise',
  range: '€100–300K',
  accent: 'bg-[#6C2BD9]',
  bgTint: 'bg-[#F5F0FF]',
  totals: {
    closed: '€334K',
    active: '€164K',
    growth: '€4.4M'
  },
  accounts: [{
    name: 'Roche',
    closed: '€194K',
    active: '€30K',
    won: 37,
    activeCount: 11,
    fullPotential: '€1.1M',
    desc: '37 campaigns across 12+ markets. PHESGO, Columvi, ophthalmology. Global oncology',
    countries: 'CH, CI, DE, DK, Global, LATAM, SG, SK +1'
  }, {
    name: 'Leo Pharma',
    closed: '€118K',
    active: '€22K',
    won: 7,
    activeCount: 4,
    fullPotential: '€420K',
    desc: '7 campaigns won incl. CHE franchise & TraloSpeak. Dermatology specialist expanding',
    countries: 'CH, DK'
  }, {
    name: 'GSK',
    closed: '€22K',
    active: '€112K',
    won: 5,
    activeCount: 5,
    fullPotential: '€2.6M',
    desc: '€50K global utilization model in pipeline. Gulf, UK, India affiliates expanding.',
    countries: 'DK, Global, Gulf, ID, IN, NO, SG, UAE +1'
  }]
}, {
  num: 'II',
  label: 'Growing',
  range: '€35–75K',
  accent: 'bg-[#9B6BE3]',
  bgTint: 'bg-[#EFE6FF]',
  totals: {
    closed: '€123K',
    active: '€100K',
    growth: '€908K'
  },
  accounts: [{
    name: 'Solventum',
    closed: '€74K',
    active: '€7K',
    won: 9,
    activeCount: 2,
    fullPotential: '€714K',
    desc: '€74K delivered: MedSurge, Ortho & Clinical. Nordics, ZA & UK',
    countries: 'DE, Nordics, UK'
  }, {
    name: 'Novartis',
    closed: '€15K',
    active: '€54K',
    won: 4,
    activeCount: 15,
    fullPotential: '€1.1M',
    desc: '15 active opportunities across 6+ countries. Entresto pilot',
    countries: 'AT, CH, DE, ES, GR, HR, HU, IE +12'
  }, {
    name: 'Menarini',
    closed: '€27K',
    active: '€16K',
    won: 5,
    activeCount: 5,
    fullPotential: '€571K',
    desc: '5 markets via Berlin Chemie: Nordics, IT, RO, UAE, DE. Multi-',
    countries: 'Baltics, CH, DE, GR, Global, IE, IT, Nordics +7'
  }, {
    name: 'Boehringer',
    closed: '€7K',
    active: '€23K',
    won: 2,
    activeCount: 7,
    fullPotential: '€714K',
    desc: '7 touchpoints: DE, FR, UK, DK, AU. Global head of content',
    countries: 'AT, AU, CH, DE, DK, ES, FR, Global +4'
  }]
}, {
  num: 'I',
  label: 'Pilot',
  range: '€7–12K',
  accent: 'bg-[#C9B5F0]',
  bgTint: 'bg-slate-50',
  totals: {
    closed: '€53K',
    active: '€110K'
  },
  accounts: [{
    name: 'Nestlé HS',
    closed: '€27K',
    active: '€12K',
    won: 6,
    activeCount: 3,
    fullPotential: '€307K',
    desc: '€27K closed. Alfamino, Althéra pediatric nutrition.'
  }, {
    name: 'CSL Vifor',
    closed: '€18K',
    active: '€11K',
    won: 2,
    activeCount: 2,
    fullPotential: '€476K',
    desc: '€18K closed. Iron deficiency & nephrology'
  }, {
    name: 'AbbVie',
    active: '€22K',
    won: 0,
    activeCount: 1,
    fullPotential: '€714K',
    desc: 'Ozurdex retinal campaign (€22K). Denmark entry.'
  }, {
    name: 'Bayer',
    active: '€17K',
    won: 0,
    activeCount: 3,
    fullPotential: '€965K',
    desc: 'Kerendia specialty campaign in pipeline. UK &'
  }, {
    name: 'Abbott',
    closed: '€4K',
    active: '€12K',
    won: 1,
    activeCount: 4,
    fullPotential: '€804K',
    desc: 'LATAM entry via Lafrancol. Basel HQ. Nutrition,'
  }, {
    name: 'AstraZeneca',
    closed: '€4K',
    active: '€12K',
    won: 1,
    activeCount: 4,
    fullPotential: '€1.6M',
    desc: '4 markets engaged: AT, GR, HR, ES (Alexion). Oncology,'
  }, {
    name: 'Novo Nordisk',
    active: '€11K',
    won: 0,
    activeCount: 4,
    fullPotential: '€804K',
    desc: 'Diabetes & obesity franchise (Ozempic, Wegovy). 15+ brands,'
  }, {
    name: 'MSD',
    active: '€8K',
    won: 0,
    activeCount: 1,
    fullPotential: '€1.1M',
    desc: 'Keytruda oncology + Gardasil vaccines + broad specialty'
  }, {
    name: 'Sanofi',
    active: '€6K',
    won: 0,
    activeCount: 2,
    fullPotential: '€2.0M',
    desc: '30+ brand portfolio incl. Dupixent. 15+ priority markets.'
  }]
}];
// Bottom stacked bar segments (must sum to 100)
const BOTTOM_BAR = [{
  label: 'S-I',
  value: '€163K',
  pct: 1,
  color: '#C9B5F0'
}, {
  label: 'S-II',
  value: '€1.1M',
  pct: 5,
  color: '#9B6BE3'
}, {
  label: 'S-III',
  value: '€4.9M',
  pct: 22,
  color: '#6C2BD9'
}, {
  label: 'S-IV Platform',
  value: '€16.0M',
  pct: 72,
  color: '#4A1B96'
}];
function AccountCard({
  a
}: {
  a: Account;
}) {
  return <div className="bg-white rounded-md border border-slate-200 px-3 py-2 flex flex-col h-full" data-id="element-883">
      <div className="flex items-start justify-between gap-2" data-id="element-884">
        <h4 className="text-slate-900 font-bold text-sm leading-tight truncate" data-id="element-885">
          {a.name}
        </h4>
        <div className="flex items-baseline gap-1 text-[11px] font-bold tabular-nums whitespace-nowrap" data-id="element-886">
          {a.closed && <span className="text-emerald-600" data-id="element-887">{a.closed}</span>}
          {a.closed && a.active && <span className="text-slate-400" data-id="element-888">+</span>}
          {a.active && <span className="text-[#6C2BD9]" data-id="element-889">{a.active}</span>}
        </div>
      </div>
      <p className="text-[9px] text-slate-500 mt-0.5" data-id="element-890">
        {a.won} won · {a.activeCount} quoted · Full potential: {a.fullPotential}
      </p>
      <p className="text-[10px] text-slate-700 leading-snug mt-1 line-clamp-2" data-id="element-891">
        {a.desc}
      </p>
      {a.countries && <div className="mt-auto pt-1 flex items-center gap-1 text-[9px] text-[#6C2BD9]" data-id="element-892">
          <Globe2 className="w-2.5 h-2.5 flex-shrink-0" data-id="element-893" />
          <span className="truncate" data-id="element-894">{a.countries}</span>
        </div>}
    </div>;
}
function StageTotalChip({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'closed' | 'active' | 'growth';
}) {
  const styles = tone === 'closed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : tone === 'active' ? 'bg-[#F5F0FF] border-[#E4D7FF] text-[#6C2BD9]' : 'bg-orange-50 border-orange-200 text-orange-600';
  return <div className={`rounded-md border px-2 py-1.5 text-center min-w-[60px] ${styles}`} data-id="element-895">
      <p className="text-[8px] uppercase tracking-widest font-bold opacity-80" data-id="element-896">
        {label}
      </p>
      <p className="text-xs font-bold tabular-nums leading-tight mt-0.5" data-id="element-897">
        {value}
      </p>
    </div>;
}
export function SlideTraction({
  onContinue
}: SlideTractionProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 200);
    const t2 = setTimeout(() => setStage(2), 600);
    const t3 = setTimeout(() => setStage(3), 1100);
    const t4 = setTimeout(() => setStage(4), 2000);
    return () => {
      ;
      [t1, t2, t3, t4].forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex items-stretch justify-center px-4 py-4 md:px-6 md:py-6" data-id="element-898">
      <div className="w-full max-w-[1400px] bg-white rounded-2xl shadow-2xl flex flex-col p-5 md:p-7 overflow-hidden" data-id="element-899">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        y: stage >= 1 ? 0 : 10
      }} className="flex items-start justify-between gap-4" data-id="element-900">
          <div data-id="element-901">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold" data-id="element-902">
              {t('Snakker AI · Account Expansion Model', 'Snakker AI · Kontoudvidelsesmodel')}
            </p>
            <h2 className="text-2xl md:text-4xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-1" data-id="element-903">
              {t('Pipeline by Stage', 'Pipeline efter fase')}
            </h2>
            <div className="flex items-center gap-3 mt-2" data-id="element-904">
              <p className="text-[11px] text-slate-600" data-id="element-905">
                {t('16 named accounts · brand × country × campaign expansion model', '16 navngivne konti · brand × land × kampagneudvidelsesmodel')}
              </p>
              <div className="flex items-center gap-2 text-[10px]" data-id="element-906">
                <span className="flex items-center gap-1" data-id="element-907">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-908" />
                  <span className="text-slate-700 font-medium" data-id="element-909">
                    {t('Closed', 'Lukket')}
                  </span>
                </span>
                <span className="flex items-center gap-1" data-id="element-910">
                  <span className="w-2 h-2 rounded-full bg-[#6C2BD9]" data-id="element-911" />
                  <span className="text-slate-700 font-medium" data-id="element-912">
                    {t('Quote', 'Tilbud')}
                  </span>
                </span>
                <span className="flex items-center gap-1" data-id="element-913">
                  <span className="w-2 h-2 rounded-full bg-orange-500" data-id="element-914" />
                  <span className="text-slate-700 font-medium" data-id="element-915">
                    {t('Growth', 'Vækst')}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Top-right summary tiles */}
          <div className="flex items-stretch gap-2" data-id="element-916">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-center" data-id="element-917">
              <p className="text-[8px] uppercase tracking-widest font-bold text-emerald-700" data-id="element-918">
                {t('Closed', 'Lukket')}
              </p>
              <p className="text-base md:text-lg font-bold text-emerald-700 tabular-nums leading-tight" data-id="element-919">
                €510K
              </p>
            </div>
            <div className="bg-[#F5F0FF] border border-[#E4D7FF] rounded-md px-3 py-2 text-center" data-id="element-920">
              <p className="text-[8px] uppercase tracking-widest font-bold text-[#6C2BD9]" data-id="element-921">
                {t('Pipeline', 'Pipeline')}
              </p>
              <p className="text-base md:text-lg font-bold text-[#6C2BD9] tabular-nums leading-tight" data-id="element-922">
                €373K
              </p>
            </div>
            <div className="bg-[#6C2BD9] rounded-md px-3 py-2 text-center text-white" data-id="element-923">
              <p className="text-[8px] uppercase tracking-widest font-bold opacity-80" data-id="element-924">
                {t('Full Potential', 'Fuldt potentiale')}
              </p>
              <p className="text-base md:text-lg font-bold tabular-nums leading-tight" data-id="element-925">
                €22.1M
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stage rows */}
        <div className="flex-1 flex flex-col gap-3 mt-4 min-h-0" data-id="element-926">
          {STAGES.map((s, sIdx) => <motion.div key={s.num} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: stage >= 2 + sIdx ? 1 : 0,
          x: stage >= 2 + sIdx ? 0 : -10
        }} transition={{
          duration: 0.4
        }} className="flex items-stretch gap-2" data-id="element-927">
              {/* Stage label column */}
              <div className={`${s.accent} rounded-lg text-white px-3 py-3 flex flex-col items-center justify-center min-w-[100px]`} data-id="element-928">
                <p className="text-[8px] uppercase tracking-widest font-bold opacity-90" data-id="element-929">
                  {t('Stage', 'Fase')} {s.num}
                </p>
                <p className="text-lg font-bold leading-tight mt-0.5" data-id="element-930">
                  {s.label}
                </p>
                <p className="text-[10px] mt-1 opacity-90" data-id="element-931">{s.range}</p>
              </div>

              {/* Accounts grid */}
              <div className={`flex-1 ${s.bgTint} rounded-lg p-2 grid gap-2`} style={{
            gridTemplateColumns: `repeat(${Math.min(s.accounts.length, 5)}, minmax(0, 1fr))`,
            gridAutoRows: s.accounts.length > 5 ? 'minmax(0, 1fr)' : 'auto',
            gridTemplateRows: s.accounts.length > 5 ? 'repeat(2, minmax(0, 1fr))' : undefined
          }} data-id="element-932">
                {s.accounts.map(a => <AccountCard key={a.name} a={a} data-id="element-933" />)}
              </div>

              {/* Totals column */}
              <div className="flex flex-col gap-1.5 justify-center min-w-[72px]" data-id="element-934">
                {s.totals.closed && <StageTotalChip label={t('Closed', 'Lukket')} value={s.totals.closed} tone="closed" data-id="element-935" />}
                <StageTotalChip label={t('Quote', 'Tilbud')} value={s.totals.active} tone="active" data-id="element-936" />
                {s.totals.growth && <StageTotalChip label={t('Growth', 'Vækst')} value={s.totals.growth} tone="growth" data-id="element-937" />}
              </div>
            </motion.div>)}
        </div>

        {/* Bottom stacked bar */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} className="mt-3 flex h-10 rounded-md overflow-hidden border border-slate-200" data-id="element-938">
          {BOTTOM_BAR.map((seg, i) => <motion.div key={seg.label} initial={{
          width: 0
        }} animate={{
          width: stage >= 4 ? `${seg.pct}%` : 0
        }} transition={{
          duration: 0.7,
          delay: 0.1 + i * 0.1,
          ease: 'easeOut'
        }} style={{
          backgroundColor: seg.color
        }} className="flex flex-col items-start justify-center px-3 text-white overflow-hidden" data-id="element-939">
              <p className="text-[9px] uppercase tracking-widest font-bold opacity-90 whitespace-nowrap" data-id="element-940">
                {seg.label}
              </p>
              <p className="text-xs font-bold tabular-nums whitespace-nowrap" data-id="element-941">
                {seg.value}
              </p>
            </motion.div>)}
          <div className="bg-slate-900 px-4 flex items-center text-white" data-id="element-942">
            <p className="text-xs font-bold whitespace-nowrap" data-id="element-943">
              = €22.1M{' '}
              {t('Full Potential across 16 accounts', 'Fuldt potentiale på tværs af 16 konti')}
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 4 ? 1 : 0
      }} className="mt-3 flex justify-end" data-id="element-944">
          <Button onClick={onContinue} data-id="element-945">
            {t("Why we're raising", 'Hvorfor vi rejser kapital')}
          </Button>
        </motion.div>
      </div>
    </div>;
}